import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  output,
  inject,
  input,
} from '@angular/core';
import type { ControlValueAccessor } from '@angular/forms';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { TranslatePipe } from '@core/i18n-legacy';
import type { IconName } from '@core/user-interface-legacy';
import {
  KeyCode,
  IconComponent,
  RtTooltip,
  ButtonComponent,
  GroupComponent,
} from '@core/user-interface-legacy';
import { asViewRef } from '@core/utils-legacy';

export const BUTTON_SELECT_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @angular-eslint/no-forward-ref
  useExisting: forwardRef(() => InterviewButtonSelectComponent),
  multi: true,
};

type Button = {
  name: string;
  icon: IconName;
  class: string;
};

@Component({
  selector: 'rt-interview-button-select',
  templateUrl: 'interview-button-select.component.html',
  styleUrls: ['./interview-button-select.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [BUTTON_SELECT_VALUE_ACCESSOR],
  host: {
    '[class.disabled]': 'disabled()',
  },
  imports: [GroupComponent, ButtonComponent, RtTooltip, IconComponent, TranslatePipe],
})
export class InterviewButtonSelectComponent implements ControlValueAccessor {
  private readonly cdRef = inject(ChangeDetectorRef);

  public readonly buttons = input<Button[]>([
    {
      name: 'no',
      icon: 'rating-thumb-down',
      class: 'error',
    },
    {
      name: 'neutral',
      icon: 'thumbs-up-down',
      class: 'warning',
    },
    {
      name: 'yes',
      icon: 'rating-thumb-up',
      class: 'success',
    },
    {
      name: 'strong_yes',
      icon: 'rating-double-thumb-up',
      class: 'success',
    },
  ]);
  public readonly horizontal = input<boolean>(undefined!);
  public readonly disabled = input<boolean>(false);
  public readonly short = input<boolean>(false);
  public readonly quickEvaluationStyle = input<boolean>(false);
  public rateButtonClick = output<void>();

  private _value: string | null;

  public get value(): string | null {
    return this._value;
  }

  public set value(value: string | null) {
    if (this._value !== value) {
      this.rateButtonClick.emit();
    }

    this._value = value;
    this.onChange(this._value);
    if (!asViewRef(this.cdRef).destroyed) {
      this.cdRef.detectChanges();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onChange = (value: string | null) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouched = () => {};

  public onClick(value: string): void {
    this.value = this.value === value ? null : value;
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (this.disabled()) {
      return;
    }
    const key = event.which || event.keyCode;
    switch (key) {
      case KeyCode.LeftArrow:
        event.preventDefault();
        this.selectLeft();
        break;

      case KeyCode.RightArrow:
        event.preventDefault();
        this.selectRight();
        break;

      default:
        break;
    }
  }

  public isChecked(name: string): boolean {
    return this._value === name;
  }

  public trackByButton(index: number, button: Button): string | number {
    return button.name || index;
  }

  public writeValue(value: string): void {
    this._value = value;
    if (!asViewRef(this.cdRef).destroyed) {
      this.cdRef.detectChanges();
    }
  }

  public registerOnChange(fn: () => {}): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: () => {}): void {
    this.onTouched = fn;
  }

  private selectLeft(): void {
    const checkedOption = this.buttons().findIndex(button => button.name === this.value);

    if (checkedOption === -1) {
      const [first] = this.buttons() || [];

      this.value = first.name;
      return;
    }

    if (checkedOption === 0 || checkedOption > this.buttons().length - 1) {
      this.value = this.buttons()[this.buttons().length - 1].name;
    } else {
      this.value = this.buttons()[checkedOption - 1].name;
    }
  }

  private selectRight(): void {
    const checkedOption = this.buttons().findIndex(button => button.name === this.value);

    if (checkedOption === -1) {
      const [first] = this.buttons() || [];
      this.value = first.name;
      return;
    }

    if (checkedOption >= this.buttons().length - 1) {
      this.value = this.buttons()[0].name;
    } else {
      this.value = this.buttons()[checkedOption + 1].name;
    }
  }
}
